import React, { useEffect, useState, useCallback, useRef } from "react";
import QRCode from "react-qr-code";
import { adminApi } from "../apiAdmin";
import { buildPassDesign, qrSvgToPngDataUrl } from "../utils/walletPassRenderer";
import { absMediaUrl } from "../utils/mediaUrl";
import { publicUrl } from "../utils/publicUrl";
import { withQuietRetry } from "../utils/adminRetry";
import AdminLoadFailed from "./AdminLoadFailed";
import { useTalentNames } from "../hooks/useTalentNames";

interface DJ {
  _id: string;
  name: string;
  email: string;
  avatarUrl?: string;
  slug?: string;
  pricePerRequest?: number;
  currency?: string;
  /** The talent's own Spotlight switch (the venue list carries only this). */
  spotlight?: { enabled?: boolean };
}

/** The venue's Spotlight screen: one permanent link; which talent it shows is set here. */
interface VenueSpotlight {
  code?: string;
  activeDj?: string | null;
  activeSince?: string | null;
  noScan?: boolean;
  /** A stopgap: the screen counts every request to the talent on now, not only those made through the venue QR. */
  allRequests?: boolean;
}

interface Establishment {
  _id: string;
  name: string;
  slug: string;
  qrCodeId: string;
  /** The words under the venue QR; absent = the name (which stays the admin's own label). */
  qrLabel?: string;
  djs: DJ[];
  spotlight?: VenueSpotlight;
  createdAt: string;
}

// Printed venue QRs must always point at production, never at whatever origin
// the admin dashboard happens to be open on (localhost, preview deploys).
const PUBLIC_BASE_URL = "https://requesta.app";
const QR_LABEL_MAX = 40;

/**
 * Branded venue QR — same green pass design as the DJ QR page (wordmark on
 * top, brand-R in the QR center, venue name below), rendered locally instead
 * of the old unbranded third-party QR image.
 */
function VenueQrCard({ est, onSaved }: { est: Establishment; onSaved: (e: Establishment) => void }) {
  const { slug } = est;
  // What the card prints: its own words when set, else the venue's name.
  const name = est.qrLabel || est.name;
  const [label, setLabel] = useState(est.qrLabel || "");
  const [saving, setSaving] = useState(false);
  const url = `${PUBLIC_BASE_URL}/venue/${slug}`;
  const svgWrapRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const renderDesign = useCallback(
    async (width: number) => {
      const svgEl = svgWrapRef.current?.querySelector("svg");
      if (!svgEl) return null;
      const qrImage = await qrSvgToPngDataUrl(svgEl as SVGSVGElement);
      return buildPassDesign({
        color: "green",
        djName: name,
        qrImage,
        rLogo: "/r.png",
        width,
      });
    },
    [name]
  );

  useEffect(() => {
    let cancelled = false;
    // Defer a tick so the hidden QR SVG is in the DOM before rasterizing.
    const timer = setTimeout(async () => {
      const design = await renderDesign(900);
      if (cancelled || !design || !canvasRef.current) return;
      const target = canvasRef.current;
      target.width = design.width;
      target.height = design.height;
      target.getContext("2d")!.drawImage(design, 0, 0);
    }, 50);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [renderDesign]);

  const handleDownload = async () => {
    const design = await renderDesign(1500);
    if (!design) return;
    design.toBlob((blob) => {
      if (!blob) return;
      const dlUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = dlUrl;
      link.download = `request-venue-${slug}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(dlUrl), 1000);
    }, "image/png");
  };

  const saveLabel = async () => {
    setSaving(true);
    try {
      const { data } = await adminApi.patch(`/establishments/${est._id}`, { qrLabel: label });
      onSaved(data);
      setLabel(data.qrLabel || "");
    } catch (err: any) {
      alert(err.response?.data?.msg || "Error saving the QR text");
    } finally {
      setSaving(false);
    }
  };
  const labelChanged = label.trim() !== (est.qrLabel || "");

  return (
    <div style={{ display: "flex", gap: 20, margin: "16px 0", alignItems: "center", flexWrap: "wrap" }}>
      <div
        ref={svgWrapRef}
        style={{ position: "absolute", opacity: 0, pointerEvents: "none" }}
        aria-hidden
      >
        <QRCode value={url} size={600} level="H" />
      </div>
      <canvas
        ref={canvasRef}
        style={{ width: 240, height: 160, borderRadius: 10, border: "1px solid #e5e7eb" }}
      />
      <div>
        <p className="adm-fs-85 adm-semibold adm-mb-4">QR Code URL:</p>
        <code className="adm-fs-78" style={{ color: "var(--adm-blue)", wordBreak: "break-all" }}>
          {url}
        </code>
        <br />
        <button
          className="adm-btn-sm adm-btn-outline adm-mt-10"
          onClick={handleDownload}
        >
          Download QR
        </button>
        <label className="adm-fs-82 adm-semibold adm-mt-10 adm-block" htmlFor={`qr-label-${est._id}`}>
          Text on the QR
        </label>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }} className="adm-mt-4">
          <input
            id={`qr-label-${est._id}`}
            className="adm-search"
            style={{ width: 240 }}
            placeholder={est.name}
            maxLength={QR_LABEL_MAX}
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && labelChanged) saveLabel();
            }}
          />
          <button className="adm-btn-sm adm-btn-green-sm" disabled={saving || !labelChanged} onClick={saveLabel}>
            Save
          </button>
        </div>
        <p className="muted-text adm-fs-74 adm-mt-4">Empty = the venue's name.</p>
      </div>
    </div>
  );
}

/**
 * Tonight's talent and the venue's Spotlight screen. "On screen now" steers
 * both the venue QR (/venue/<slug> goes straight to that talent; None = the
 * list) and the screen link the venue puts on a display once
 * (requesta.app/s/<code>): every open screen switches within a few seconds,
 * no reload. "Skip QR": the screen drops its Scan slide after the night's
 * first request (for a venue that already shows a QR of its own). The screen
 * shows only requests made through the venue QR; "Also count …" is the
 * stopgap while the venue still shows the talent's own QR. Nobody on now (or
 * the talent Offline): the screen says "Requests return when DJs go live".
 */
function VenueSpotlightCard({ est, onSaved }: { est: Establishment; onSaved: (e: Establishment) => void }) {
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const sp = est.spotlight || {};
  const link = sp.code ? `${PUBLIC_BASE_URL}/s/${sp.code}` : null;
  const active = est.djs.find((d) => d._id === sp.activeDj) || null;

  const save = async (body: { activeDj?: string | null; noScan?: boolean; allRequests?: boolean }) => {
    setBusy(true);
    try {
      const { data } = await adminApi.patch(`/establishments/${est._id}/spotlight`, body);
      onSaved(data);
    } catch (err: any) {
      alert(err.response?.data?.msg || "Error saving the screen");
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      window.prompt("Copy the screen link:", link);
    }
  };

  return (
    <div className="adm-mb-14" style={{ border: "1px solid var(--adm-line, #e5e7eb)", borderRadius: 10, padding: 14 }}>
      <div style={{ display: "flex", gap: 10, alignItems: "center" }} className="adm-mb-4">
        <label className="adm-fs-85 adm-semibold adm-nowrap" htmlFor={`spot-active-${est._id}`}>On now</label>
        <select
          id={`spot-active-${est._id}`}
          className="adm-select adm-flex-1"
          value={sp.activeDj || ""}
          disabled={busy || est.djs.length === 0}
          onChange={(e) => save({ activeDj: e.target.value || null })}
        >
          <option value="">None (QR shows the list; screen: "Requests return when DJs go live")</option>
          {est.djs.map((d) => (
            <option key={d._id} value={d._id}>{d.name}</option>
          ))}
        </select>
      </div>
      <p className="muted-text adm-fs-78 adm-mb-12">
        {est.djs.length === 0 ? "Add talent below first." : "The venue QR and the Spotlight screen both go to this talent."}
      </p>
      {active && link && !active.spotlight?.enabled && (
        <p className="adm-fs-80 adm-orange adm-mb-12">
          {active.name}'s own Fan's Spotlight switch is off: the screen says "Requests return when DJs go live" until they turn it on.
        </p>
      )}
      <p className="adm-fs-85 adm-semibold adm-mb-8">Spotlight screen</p>
      {!link ? (
        <button className="adm-btn-sm adm-btn-outline" disabled={busy} onClick={() => save({})}>
          Create screen link
        </button>
      ) : (
        <>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }} className="adm-mb-8">
            <code className="adm-fs-78" style={{ color: "var(--adm-blue)", wordBreak: "break-all" }}>{link}</code>
            <button className="adm-btn-sm adm-btn-outline" onClick={copy}>{copied ? "Copied" : "Copy"}</button>
            <a className="adm-btn-sm adm-btn-outline" href={link} target="_blank" rel="noreferrer">Open</a>
          </div>
          <label className="adm-fs-82" style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input type="checkbox" checked={sp.noScan === true} disabled={busy} onChange={(e) => save({ noScan: e.target.checked })} />
            Skip the QR slide after the first request (the venue shows its own QR)
          </label>
          <label className="adm-fs-82 adm-mt-6" style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input
              type="checkbox"
              checked={sp.allRequests === true}
              disabled={busy}
              onChange={(e) => save({ allRequests: e.target.checked })}
            />
            Also count requests from {active ? `${active.name}'s` : "the talent's"} own QR and links (until the venue QR is up)
          </label>
          {sp.allRequests === true && (
            <p className="adm-fs-80 adm-orange adm-mt-6">
              On: every request to the talent on now shows here, from any link. Pick None when they finish here, and untick this once the venue QR is on the venue's screen.
            </p>
          )}
        </>
      )}
    </div>
  );
}

export default function AdminEstablishmentsTab({ allDjs }: { allDjs: DJ[] }) {
  const { talent, partial } = useTalentNames(allDjs);
  const [establishments, setEstablishments] = useState<Establishment[]>([]);
  const [newName, setNewName] = useState("");
  const [expanded, setExpanded] = useState<string | null>(null);
  const [addDjId, setAddDjId] = useState("");
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const fetchEstablishments = useCallback(async () => {
    setFailed(false);
    try {
      const { data } = await withQuietRetry(() => adminApi.get("/establishments"));
      setEstablishments(data);
    } catch {
      // Shown below: "Couldn't load venues." + Retry, the list already on
      // screen (if any) stays.
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEstablishments();
  }, [fetchEstablishments]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName.trim()) return;
    try {
      await adminApi.post("/establishments", { name: newName.trim() });
      setNewName("");
      fetchEstablishments();
    } catch (err: any) {
      alert(err.response?.data?.msg || "Error creating establishment");
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Delete this establishment? This cannot be undone.")) return;
    try {
      await adminApi.delete(`/establishments/${id}`);
      setEstablishments((prev) => prev.filter((e) => e._id !== id));
      if (expanded === id) setExpanded(null);
    } catch (err: any) {
      alert(err.response?.data?.msg || "Error");
    }
  };

  const handleAddDj = async (estId: string) => {
    if (!addDjId) return;
    try {
      const { data } = await adminApi.post(`/establishments/${estId}/djs`, { djId: addDjId });
      setEstablishments((prev) => prev.map((e) => (e._id === estId ? data : e)));
      setAddDjId("");
    } catch (err: any) {
      alert(err.response?.data?.msg || "Error adding DJ");
    }
  };

  const handleRemoveDj = async (estId: string, djId: string) => {
    try {
      const { data } = await adminApi.delete(`/establishments/${estId}/djs/${djId}`);
      setEstablishments((prev) => prev.map((e) => (e._id === estId ? data : e)));
    } catch (err: any) {
      alert(err.response?.data?.msg || "Error");
    }
  };

  const getQrUrl = (slug: string) => publicUrl(`/venue/${slug}`);
  const onSaved = (saved: Establishment) => setEstablishments((prev) => prev.map((e) => (e._id === saved._id ? saved : e)));

  if (loading) return <p className="muted-text adm-p-30 adm-center">Loading...</p>;

  return (
    <section className="fade-in">
      {/* Create form */}
      <div className="adm-card adm-mb-20">
        <h2 className="adm-section-title adm-mb-14">Create Establishment</h2>
        <form onSubmit={handleCreate} style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <input
            className="adm-search adm-flex-1"
            placeholder="Establishment name..."
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            required
          />
          <button className="adm-btn-green" type="submit">+ Create</button>
        </form>
      </div>

      {/* List */}
      {failed && <AdminLoadFailed what="venues" onRetry={fetchEstablishments} />}
      {failed && establishments.length === 0 ? null : establishments.length === 0 ? (
        <p className="muted-text adm-center adm-p-30">No establishments yet.</p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {establishments.map((est) => {
            const isOpen = expanded === est._id;
            const estDjIds = est.djs.map((d) => d._id);
            const availableDjs = talent.filter((d) => !estDjIds.includes(d._id));

            return (
              <div key={est._id} className="adm-card" style={{ padding: 0, overflow: "hidden" }}>
                {/* Header */}
                <div
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "14px 18px", cursor: "pointer", background: isOpen ? "var(--adm-bg-sec, #f8fafc)" : "transparent",
                  }}
                  onClick={() => setExpanded(isOpen ? null : est._id)}
                >
                  <div>
                    <span className="adm-bold" style={{ fontSize: "1rem" }}>{est.name}</span>
                    <span className="muted-text adm-fs-80" style={{ marginLeft: 10 }}>
                      {est.djs.length} Talent
                    </span>
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <a
                      href={getQrUrl(est.slug)}
                      target="_blank"
                      rel="noreferrer"
                      className="adm-btn-sm adm-btn-outline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Open page
                    </a>
                    <button
                      className="adm-btn-sm adm-btn-red-sm"
                      onClick={(e) => { e.stopPropagation(); handleDelete(est._id); }}
                    >
                      Delete
                    </button>
                    <span style={{ fontSize: "0.9rem", color: "#94a3b8" }}>{isOpen ? "▲" : "▼"}</span>
                  </div>
                </div>

                {/* Expanded content */}
                {isOpen && (
                  <div style={{ padding: "0 18px 18px", borderTop: "1px solid var(--adm-line, #e5e7eb)" }}>
                    {/* QR Code — branded green pass design */}
                    <VenueQrCard est={est} onSaved={onSaved} />

                    <VenueSpotlightCard est={est} onSaved={onSaved} />

                    {/* Add Talent */}
                    <div className="adm-mb-14" style={{ display: "flex", gap: 10 }}>
                      <select
                        className="adm-select adm-flex-1"
                        value={addDjId}
                        onChange={(e) => setAddDjId(e.target.value)}
                      >
                        <option value="">— Select Talent to add —</option>
                        {availableDjs.map((d) => (
                          <option key={d._id} value={d._id}>{d.name} ({d.email})</option>
                        ))}
                        {partial && <option disabled>Couldn't load the full list</option>}
                      </select>
                      <button
                        className="adm-btn-green"
                        onClick={() => handleAddDj(est._id)}
                        disabled={!addDjId}
                      >
                        Add Talent
                      </button>
                    </div>

                    {/* DJ list */}
                    {est.djs.length === 0 ? (
                      <p className="muted-text adm-fs-85">No Talent added yet.</p>
                    ) : (
                      <table className="adm-table-light adm-table-compact">
                        <thead>
                          <tr>
                            <th>Talent</th>
                            <th>Price</th>
                            <th style={{ width: 80 }}>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {est.djs.map((dj) => {
                            const avatar = absMediaUrl(dj.avatarUrl);
                            return (
                              <tr key={dj._id}>
                                <td>
                                  <div className="dj-cell dj-cell-compact">
                                    {avatar ? (
                                      <img src={avatar} className="dj-cell-avatar-sm" alt="" />
                                    ) : (
                                      <div className="dj-cell-placeholder-sm">
                                        {dj.name?.[0]?.toUpperCase() || "?"}
                                      </div>
                                    )}
                                    <div>
                                      <span className="adm-semibold adm-fs-85">{dj.name}</span>
                                      {dj.slug && (
                                        <div className="muted-text adm-fs-72">@{dj.slug}</div>
                                      )}
                                    </div>
                                  </div>
                                </td>
                                <td className="adm-semibold">
                                  {(dj.currency || "usd").toUpperCase()} {dj.pricePerRequest ?? 1}
                                </td>
                                <td>
                                  <button
                                    className="adm-btn-sm adm-btn-red-sm"
                                    onClick={() => handleRemoveDj(est._id, dj._id)}
                                  >
                                    Remove
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
